import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { AuroraBackground } from "@/components/aurora-background";
import { SectionEyebrow } from "@/components/section-eyebrow";
import { seo } from "@/lib/seo";
import { collectionPage, breadcrumb } from "@/lib/structured-data";
import { SITE_URL } from "@/lib/seo-constants";
import { useCasesSummary } from "@/lib/use-cases-data";

type Item = { slug: string; label: string; body: string; metric: string };

const byCompanyType: Item[] = [
  {
    slug: "high-growth-startups",
    label: "High-growth startups",
    body: "Compound AI visibility while you focus on product.",
    metric: "11× citations",
  },
  {
    slug: "b2b-saas",
    label: "B2B SaaS",
    body: "Turn launches into compounding pipeline across AI search.",
    metric: "+38% pipeline",
  },
  {
    slug: "mid-market-enterprise",
    label: "Mid-market enterprises",
    body: "Enterprise governance with agentic execution speed.",
    metric: "7× visibility",
  },
  {
    slug: "venture-studios",
    label: "Venture studios",
    body: "One engine across every portfolio company brand.",
    metric: "5 brands / engine",
  },
  {
    slug: "agencies-studios",
    label: "Agencies & studios",
    body: "White-label autonomous marketing for your clients.",
    metric: "8 clients / seat",
  },
];

const byIndustry: Item[] = [
  {
    slug: "law",
    label: "Law firms",
    body: "Compliance-aware agentic content for modern law firms.",
    metric: "8× AI mentions",
  },
  {
    slug: "cybersecurity",
    label: "Cybersecurity companies",
    body: "Credible, citation-rich content for technical buyers.",
    metric: "12× citations",
  },
  {
    slug: "real-estate-construction",
    label: "Real estate & construction",
    body: "Capture local intent and project-level demand.",
    metric: "7.2× local visibility",
  },
  {
    slug: "logistics",
    label: "Logistics",
    body: "Lane- and mode-specific content where shippers research.",
    metric: "+33% RFP invites",
  },
  {
    slug: "web3",
    label: "Web3 projects",
    body: "Narrative-driven campaigns at crypto-market speed.",
    metric: "14× narrative reach",
  },
  {
    slug: "fitness",
    label: "Fitness & wellness brands",
    body: "Trend-driven content your community is searching for.",
    metric: "6× search demand",
  },
  {
    slug: "ecommerce",
    label: "Ecommerce & luxury brands",
    body: "PDP and category visibility across AI search engines.",
    metric: "11× PDP citations",
  },
];

export const Route = createFileRoute("/use-cases/")({
  head: () =>
    seo({
      title: "AI Marketing Use Cases by Company Type",
      description:
        "How teams across company types and industries use Axy.digital as their agentic marketing engine.",
      path: "/use-cases",
      jsonLd: [
        collectionPage({
          name: "Axy.digital Use Cases",
          description: "By company type and by industry — see how Axy.digital adapts to your team.",
          url: `${SITE_URL}/use-cases`,
          items: useCasesSummary.map((u) => ({ name: u.label, url: `/use-cases/${u.slug}` })),
        }),
        breadcrumb([
          { name: "Home", url: "/" },
          { name: "Use cases", url: "/use-cases" },
        ]),
      ],
    }),
  component: UseCasesIndex,
});

function Card({ u }: { u: Item }) {
  return (
    <Link
      to="/use-cases/$slug"
      params={{ slug: u.slug }}
      className="group relative glass-card p-6 hover-lift hover:-translate-y-1 hover:border-white/25 overflow-hidden transition-all duration-300"
    >
      <div
        aria-hidden
        className="absolute -top-12 -right-12 size-36 rounded-full blur-3xl opacity-0 group-hover:opacity-70 transition-opacity duration-500"
        style={{ background: "radial-gradient(circle, rgba(217,70,239,0.55), transparent 65%)" }}
      />
      <h3 className="relative text-lg font-semibold text-white mb-2">{u.label}</h3>
      <p className="relative text-sm text-white/65 mb-5">{u.body}</p>
      <div className="relative flex items-center justify-between">
        <span className="glass-pill px-2.5 py-1 text-[11px] font-medium text-white/85">
          {u.metric}
        </span>
        <span className="inline-flex items-center gap-1 text-xs text-pink-soft group-hover:gap-2 transition-all">
          Explore <ArrowRight className="size-3.5" />
        </span>
      </div>
    </Link>
  );
}

function Group({ title, items }: { title: string; items: Item[] }) {
  return (
    <div>
      <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-pink-soft mb-5">
        {title}
      </p>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((u) => (
          <Card key={u.slug} u={u} />
        ))}
      </div>
    </div>
  );
}

function UseCasesIndex() {
  return (
    <section className="relative px-6 py-24 overflow-hidden">
      <AuroraBackground />
      <div className="relative max-w-7xl mx-auto">
        <SectionEyebrow>Use cases</SectionEyebrow>
        <h1 className="text-4xl md:text-6xl font-semibold tracking-tight text-white mb-6 text-balance">
          Built for teams that <span className="text-gradient">move fast.</span>
        </h1>
        <p className="text-lg text-white/70 max-w-2xl">
          Browse by company type or by industry to see how Axy.digital adapts its market
          intelligence and campaign deployment.
        </p>
        <div className="mt-12 space-y-16">
          <Group title="By company type" items={byCompanyType} />
          <Group title="By industry" items={byIndustry} />
        </div>
      </div>
    </section>
  );
}
